import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Zap from 'lucide-react/dist/esm/icons/zap.js';
import DollarSign from 'lucide-react/dist/esm/icons/dollar-sign.js';
import Palette from 'lucide-react/dist/esm/icons/palette.js';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import HomeHeroShowcase from '@/components/HomeHeroShowcase';
import HomeFinalCta from '@/components/HomeFinalCta';
import HomeProcessJourney from '@/components/HomeProcessJourney';
import HeadwearDiscovery from '@/components/HeadwearDiscovery';
import { SITE_URL, canonicalUrl } from '../seo/siteRoutes.js';

const Home = () => {
  const { t, isRTL } = useLanguage();

  const features = [
    {
      icon: Zap,
      title: isRTL ? 'בחירת מוצר והתאמה' : 'Product configuration',
      description: isRTL
        ? 'בוחרים מוצר, צבעים ומידות לפי האפשרויות המוצגות בדף המוצר.'
        : 'Choose a product, colors and sizes from the options shown on its page.'
    },
    {
      icon: DollarSign,
      title: isRTL ? 'מחיר מחושב באתר' : 'Calculated pricing',
      description: isRTL
        ? 'המחיר מתעדכן לפי הכמות ואזורי המיתוג שנבחרו.'
        : 'The price updates according to quantity and selected branding areas.'
    },
    {
      icon: Palette,
      title: isRTL ? 'עיצוב לפי אזורי מיתוג' : 'Branding-area design',
      description: isRTL
        ? 'מעלים קובץ וממקמים אותו באזורי המיתוג הפעילים של הפריט.'
        : 'Upload artwork and position it within the product’s available branding areas.'
    }
  ];

  return (
    <>
      <Helmet>
        {/* Organization & WebSite structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Printeam",
            legalName: "א.ז. שירותי מחשוב ולוגיסטיקה",
            url: canonicalUrl('/'),
            logo: `${SITE_URL}/logo_printee-optimized.webp`,
            contactPoint: [
              {
                "@type": "ContactPoint",
                contactType: "customer support",
                telephone: "+972503969640",
                email: "info@printeam.co.il",
                availableLanguage: ["he"]
              }
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Printeam",
            url: canonicalUrl('/')
          })}
        </script>
      </Helmet>

      <div className="min-h-screen">
        
        <HomeHeroShowcase />

        <HomeProcessJourney />

        <HeadwearDiscovery />

        {/* Features Section */}
        {/* Methods of Branding teaser */}
        <section className="py-12 bg-blue-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-xl p-6 shadow-sm flex flex-col md:flex-row items-center gap-6">
              <div className="w-full md:w-1/3">
                <picture>
                  <source srcSet="/hero_images/dtg.avif" type="image/avif" />
                  <source srcSet="/hero_images/dtg.webp" type="image/webp" />
                  <img
                    src="/hero_images/dtg.avif"
                    alt="דוגמה למיתוג על טקסטיל"
                    className="w-full h-44 object-cover rounded-md"
                    loading="lazy"
                    decoding="async"
                    width={1200}
                    height={800}
                  />
                </picture>
              </div>
              <div className="w-full md:w-2/3 text-right">
                <h3 className="text-2xl font-bold mb-2">השיטות שאנו מציעים למיתוג</h3>
                <p className="text-gray-700 mb-4">
                  אפשרויות המיתוג כוללות רקמה, הדפסה במסך, DTG, DTF וסובלימציה.
                  ההתאמה תלויה בסוג המוצר, בחומר, בעיצוב ובכמות המבוקשת.
                </p>
                <div className="flex justify-end">
                  <Button asChild>
                    <Link to="/methods-of-branding">למד עוד על שיטות המיתוג</Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                {isRTL ? 'מה אפשר להגדיר באתר?' : 'What can you configure on the site?'}
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="rounded-xl bg-gradient-to-br from-blue-50 to-purple-50 p-8 text-center"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full mb-6">
                    <feature.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <HomeFinalCta />
      </div>
    </>
  );
};

export default Home;
